import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CalendarCheck,
  CalendarClock,
  Clock,
  Eye,
  FileText,
  Flame,
  LayoutDashboard,
  Link2,
  MessageSquare,
  MessagesSquare,
  PencilLine,
  Plus,
  RotateCcw,
  Rss,
  Send,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  ApprovalState,
  SOCIAL_PLATFORM_ORDER,
  SourceHealth,
  type InboxConversation,
} from '@mg/shared';
import { api, ApiRequestError } from '../lib/api';
import { cn } from '../lib/cn';
import { useTenant } from '../auth/TenantContext';
import { useTenantKey } from '../lib/useTenantKey';
import { useEffectiveApprovalType } from '../lib/useEffectiveApprovalType';
import { approvalStateLabel, approvalStateTone, formatDateTime, formatRelative } from '../lib/display';
import { PageHeader } from '../components/shell/PageHeader';
import { TestModeBanner } from '../features/testMode/TestModeBanner';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Sparkline } from '../components/ui/Sparkline';
import { folderCounts } from '../features/messages/seed-data';
import { postBadgeState } from '../features/posts/PostsView';
import { analyticsAccounts, pickAccount } from '../features/analytics/accounts';
import { FollowersBarChart } from '../features/analytics/FollowersBarChart';
import { PLATFORM_META } from '../features/social/platforms';
import { TAB_STATES } from '../features/approval/statusTabs';
import { SEED_PROMPT_MAX } from './strategist/chatSeed';

/**
 * Dashboard — the workspace-at-a-glance home. The top "Campaign Pipeline" section
 * gives a per-post read of where everything sits (one clickable KPI per status tab),
 * then a three-column widget grid summarises each surface (Strategist, Trends,
 * Connections, Messages, Account Performance, Digest Sources, Engagement, Upcoming).
 *
 * Every widget runs its OWN query using the SAME query key as its destination page, so
 * results come from shared react-query cache (no duplicate fetches) and one failure is
 * isolated to its card. Workspace `?ws=` is auto-reconciled by TenantContext, so links
 * are written plainly (no `ws` appended here).
 */
export function DashboardPage() {
  const { activeTenant } = useTenant();

  return (
    <>
      <PageHeader
        title="Dashboard"
        icon={LayoutDashboard}
        description={`${activeTenant?.name ?? 'Workspace'} · Your workspace at a glance`}
      />

      <TestModeBanner />

      <PipelineSection />

      <div className="mt-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Across your workspace
        </h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Left column */}
          <div className="flex flex-col gap-4">
            <StrategistTrendsWidget />
          </div>
          {/* Middle column */}
          <div className="flex flex-col gap-4">
            <ConnectionsWidget />
            <MessagesWidget />
            <AccountPerformanceWidget />
          </div>
          {/* Right column */}
          <div className="flex flex-col gap-4">
            <SourcesWidget />
            <EngagementOverviewWidget />
            <UpcomingPostsWidget />
          </div>
        </div>
      </div>
    </>
  );
}

// ── Shared card shells ───────────────────────────────────────────────────────

/** Header row shared by every widget card: tinted icon tile + title + right slot. */
function CardHead({ icon: Icon, title, right }: { icon: LucideIcon; title: string; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-tint text-tint-fg">
          <Icon className="h-[1.125rem] w-[1.125rem]" aria-hidden />
        </span>
        <h3 className="truncate text-base font-semibold text-ink">{title}</h3>
      </div>
      {right}
    </div>
  );
}

/** A muted "View →" header link to a widget's destination page. */
function HeadLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="focus-ring shrink-0 rounded text-sm font-medium text-link hover:text-action-hover"
    >
      {children} →
    </Link>
  );
}

/**
 * A whole-card link widget for summary cards with NO interactive children
 * (Connections, Digest Sources, Account Performance). The header "View →" is a
 * visual affordance (the whole card is the link).
 */
function LinkCard({
  to,
  icon,
  title,
  isLoading,
  isError,
  children,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  isLoading?: boolean;
  isError?: boolean;
  children: ReactNode;
}) {
  return (
    <Link to={to} className="focus-ring block rounded-xl">
      <Card className="flex h-full flex-col p-5 transition-shadow hover:border-ink-ghost hover:shadow-md">
        <CardHead
          icon={icon}
          title={title}
          right={<span className="shrink-0 text-sm font-medium text-link">View →</span>}
        />
        <div className="mt-4 flex-1">
          <WidgetBody isLoading={isLoading} isError={isError}>
            {children}
          </WidgetBody>
        </div>
      </Card>
    </Link>
  );
}

/**
 * A panel widget for cards that contain their own interactive children
 * (textareas, per-row buttons/links). Not a whole-card link — the header carries a
 * real "View →" link and rows own their own navigation.
 */
function PanelCard({
  icon,
  title,
  headLink,
  children,
}: {
  icon: LucideIcon;
  title: string;
  headLink?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="flex h-full flex-col p-5">
      <CardHead icon={icon} title={title} right={headLink} />
      <div className="mt-4 flex-1">{children}</div>
    </Card>
  );
}

/** Loading skeleton / muted error / content switch for a widget body. */
function WidgetBody({
  isLoading,
  isError,
  children,
}: {
  isLoading?: boolean;
  isError?: boolean;
  children: ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2" aria-hidden>
        <div className="h-5 w-2/3 animate-pulse rounded bg-chip" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-chip" />
      </div>
    );
  }
  if (isError) return <p className="text-sm text-ink-faint">Couldn&apos;t load</p>;
  return <>{children}</>;
}

/** Allow only http(s) hrefs from external provider data (drops javascript:/data:). */
function safeHref(u?: string): string | undefined {
  if (!u) return undefined;
  try {
    const proto = new URL(u, window.location.origin).protocol;
    return proto === 'http:' || proto === 'https:' ? u : undefined;
  } catch {
    return undefined;
  }
}

// ── Campaign Pipeline (top section) ───────────────────────────────────────────

type TileTone = 'neutral' | 'attention' | 'ready' | 'done';

const TILE_TONES: Record<TileTone, { chip: string; num: string }> = {
  neutral: { chip: 'bg-chip text-ink-muted', num: 'text-ink' },
  attention: { chip: 'bg-amber-100 dark:bg-warning-dark-tint text-amber-600 dark:text-warning-dark', num: 'text-amber-600 dark:text-warning-dark' },
  ready: { chip: 'bg-tint text-tint-fg', num: 'text-link' },
  done: { chip: 'bg-success-tint dark:bg-success-dark-tint text-success dark:text-success-dark', num: 'text-ink' },
};

function PipelineTile({
  to,
  icon: Icon,
  label,
  value,
  tone,
  isLoading,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  value: number;
  tone: TileTone;
  isLoading: boolean;
}) {
  const t = TILE_TONES[tone];
  const emphasise = (tone === 'attention' || tone === 'ready') && value > 0;
  return (
    <Link
      to={to}
      className="focus-ring group flex flex-col justify-between rounded-2xl border border-line bg-card p-4 transition-shadow hover:shadow-card"
    >
      <span className={cn('flex h-9 w-9 items-center justify-center rounded-xl', t.chip)}>
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <div className="mt-4">
        <p
          className={cn(
            'text-3xl font-bold leading-none',
            isLoading
              ? 'text-line'
              : value === 0
                ? 'text-ink-ghost'
                : emphasise
                  ? t.num
                  : 'text-ink',
          )}
        >
          {isLoading ? '—' : value}
        </p>
        <p className="mt-1.5 text-sm text-ink-muted">{label}</p>
      </div>
    </Link>
  );
}

/**
 * Campaign Pipeline hero: PER-POST status counts from the shared ['posts'] query,
 * bucketed with the same postBadgeState + effective-approval logic the Campaigns &
 * Posts page uses, so a post's bucket here matches its tab there exactly. Each tile
 * deep-links the All-Posts view filtered to that status tab.
 */
function PipelineSection() {
  const tenantKey = useTenantKey();
  const approvalType = useEffectiveApprovalType();
  const query = useQuery({
    queryKey: ['posts', tenantKey],
    queryFn: ({ signal }) => api.listPosts(signal),
    enabled: tenantKey !== '',
  });

  const posts = useMemo(() => query.data ?? [], [query.data]);
  const count = (states: ApprovalState[]) =>
    posts.filter((p) => states.includes(postBadgeState(p, approvalType))).length;

  const tiles: {
    to: string;
    icon: LucideIcon;
    label: string;
    value: number;
    tone: TileTone;
  }[] = [
    {
      to: '/campaigns?view=posts&tab=drafts',
      icon: PencilLine,
      label: 'Drafts',
      value: count(TAB_STATES.drafts),
      tone: 'neutral',
    },
    {
      to: '/campaigns?view=posts&tab=approvals',
      icon: Eye,
      label: 'Needs review',
      value: count(TAB_STATES.approvals),
      tone: 'attention',
    },
    {
      to: '/campaigns?view=posts&tab=revise',
      icon: RotateCcw,
      label: 'Needs revision',
      value: count(TAB_STATES.revise),
      tone: 'attention',
    },
    {
      to: '/campaigns?view=posts&tab=approved',
      icon: CalendarClock,
      label: 'Ready to schedule',
      value: count(TAB_STATES.approved),
      tone: 'ready',
    },
    {
      to: '/campaigns?view=posts&tab=scheduled',
      icon: CalendarCheck,
      label: 'Scheduled',
      value: count(TAB_STATES.scheduled),
      tone: 'neutral',
    },
    {
      to: '/campaigns?view=posts&tab=published',
      icon: Send,
      label: 'Published',
      value: count(TAB_STATES.published),
      tone: 'done',
    },
  ];

  return (
    <section aria-labelledby="pipeline-heading">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-ink-faint" aria-hidden />
          <h2
            id="pipeline-heading"
            className="text-xs font-semibold uppercase tracking-wide text-ink-muted"
          >
            Campaign pipeline
          </h2>
        </div>
        <HeadLink to="/campaigns">View all campaigns</HeadLink>
      </div>
      {query.isError ? (
        <div className="rounded-2xl border border-line bg-card p-4 text-sm text-ink-muted">
          Couldn&apos;t load posts.{' '}
          <Link to="/campaigns" className="font-medium text-link hover:text-action-hover hover:underline">
            Open Campaigns
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {tiles.map((tile) => (
            <PipelineTile key={tile.label} {...tile} isLoading={query.isLoading} />
          ))}
        </div>
      )}
    </section>
  );
}

// ── Content Strategist + Social Trends (left column, one card) ────────────────

function StrategistTrendsWidget() {
  return (
    <Card className="flex flex-col p-5">
      <StrategistQuickEntry />
      <div className="my-4 border-t border-line-soft" />
      <SocialTrendsInline />
    </Card>
  );
}

/**
 * A prompt box that opens the Strategist on what was typed.
 *
 * It only navigates, handing the text over: the Strategist page creates the conversation and sends
 * the message, so the prompt is on screen and the typing indicator runs while the model works — the
 * same as "Write Post with Strategist" on Trends. It used to create and send from here and wait for
 * the model's whole answer before navigating, on a page that could show nothing but "Sending…".
 */
export function StrategistQuickEntry() {
  const tenantKey = useTenantKey();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');

  const convos = useQuery({
    queryKey: ['strategist-conversations', tenantKey],
    queryFn: ({ signal }) => api.listConversations(signal),
    enabled: tenantKey !== '',
  });

  const trimmed = prompt.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed) return;
    navigate('/strategist', { state: { seedPrompt: trimmed } });
  };

  const conversationCount = convos.data?.length ?? 0;

  return (
    <div>
      <CardHead icon={Sparkles} title="Content Strategist" />
      <form onSubmit={submit} className="mt-3">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          // The longest message the server takes. The box stops there, so what is sent is what is
          // in the box — the Strategist page would otherwise cut a longer text with nothing said.
          maxLength={SEED_PROMPT_MAX}
          rows={2}
          placeholder="What do you want to write about?"
          aria-label="What do you want to write about?"
          className="focus-ring block w-full resize-none rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink placeholder:text-ink-faint"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">
              {convos.isLoading ? '—' : conversationCount} Conversation
              {conversationCount === 1 ? '' : 's'}
            </p>
            <Link
              to="/strategist"
              className="focus-ring rounded text-sm font-medium text-link hover:text-action-hover"
            >
              View Strategist →
            </Link>
          </div>
          <button
            type="submit"
            disabled={!trimmed}
            className="focus-ring inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-action px-4 text-sm font-medium text-action-fg transition-colors hover:bg-action-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send className="h-4 w-4" aria-hidden />
            Send to Strategist
          </button>
        </div>
      </form>
    </div>
  );
}

/** The top digest themes, each with a "generate content" chat shortcut. */
function SocialTrendsInline() {
  const tenantKey = useTenantKey();
  const navigate = useNavigate();

  const brief = useQuery({
    queryKey: ['brief', tenantKey],
    queryFn: async ({ signal }) => {
      try {
        return await api.getBrief(undefined, signal);
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: tenantKey !== '',
  });

  /**
   * Open the Strategist on this trend, immediately.
   *
   * It used to wait here for the model's entire first answer before navigating. The Strategist page
   * does the seeding now, so the prompt is visible while the answer is still being written — see
   * the same handler on the Trends page.
   */
  const generate = (themeId: string) => {
    const b = brief.data;
    if (!b) return;
    navigate('/strategist', { state: { seedTheme: { briefId: b.id, themeId } } });
  };

  const themes = (brief.data?.themes ?? []).slice(0, 5);

  return (
    <div>
      <CardHead icon={TrendingUp} title="Social Trends" right={<HeadLink to="/trends">View Trends</HeadLink>} />
      <div className="mt-3">
        <WidgetBody isLoading={brief.isLoading} isError={brief.isError}>
          {themes.length === 0 ? (
            <p className="text-sm text-ink-muted">No digest yet.</p>
          ) : (
            <ul className="space-y-2">
              {themes.map((theme) => {
                return (
                  <li
                    key={theme.id}
                    className="flex items-center gap-2 rounded-lg border border-line px-3 py-2.5"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-ink" title={theme.title}>
                      {theme.title}
                    </span>
                    <button
                      type="button"
                      onClick={() => generate(theme.id)}
                      aria-label={`Generate content about ${theme.title}`}
                      title="Generate content in the Strategist"
                      className="focus-ring flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-faint transition-colors hover:bg-tint hover:text-tint-fg disabled:opacity-50"
                    >
                      <MessageSquare className="h-4 w-4" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </WidgetBody>
      </div>
    </div>
  );
}

// ── Connections ───────────────────────────────────────────────────────────────

function ConnectionsWidget() {
  const tenantKey = useTenantKey();
  const query = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
  });

  const connections = query.data ?? [];
  const activePlatforms = new Set(
    connections.filter((c) => c.connected && !c.needsReconnect).map((c) => c.platform),
  );
  const needsReconnect = connections.some((c) => c.connected && c.needsReconnect === true);
  const total = SOCIAL_PLATFORM_ORDER.length;

  return (
    <LinkCard to="/connections" icon={Link2} title="Connections" isLoading={query.isLoading} isError={query.isError}>
      <div className="flex items-center gap-1.5">
        {SOCIAL_PLATFORM_ORDER.map((p) => {
          const meta = PLATFORM_META[p];
          const on = activePlatforms.has(p);
          return (
            <span
              key={p}
              title={meta.label}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-lg',
                on ? meta.tile : 'bg-chip text-ink-ghost',
              )}
            >
              <meta.Icon className="h-4 w-4" aria-hidden />
            </span>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'text-sm font-medium',
            activePlatforms.size === total ? 'text-success dark:text-success-dark' : 'text-ink-muted',
          )}
        >
          {activePlatforms.size} of {total} active
        </span>
        {needsReconnect && <Badge tone="red">Reconnect needed</Badge>}
      </div>
    </LinkCard>
  );
}

// ── Messages ───────────────────────────────────────────────────────────────────

function MessagesWidget() {
  const tenantKey = useTenantKey();
  const query = useQuery({
    queryKey: ['inbox', tenantKey],
    queryFn: ({ signal }) => api.getInboxConversations(signal),
    enabled: tenantKey !== '',
    staleTime: 60_000,
  });

  const conversations = useMemo(() => query.data ?? [], [query.data]);
  const { unread } = folderCounts(conversations);
  const recent = useMemo(() => {
    const lastAt = (c: InboxConversation) => c.messages[c.messages.length - 1]?.sentAt ?? '';
    return conversations
      .slice()
      .sort((a, b) => Date.parse(lastAt(b)) - Date.parse(lastAt(a)))
      .slice(0, 4);
  }, [conversations]);

  return (
    <PanelCard
      icon={MessagesSquare}
      title="Messages"
      headLink={
        <div className="flex items-center gap-2">
          {unread > 0 && <Badge tone="brand">{unread}</Badge>}
          <HeadLink to="/messages">View all {conversations.length}</HeadLink>
        </div>
      }
    >
      <WidgetBody isLoading={query.isLoading} isError={query.isError}>
        {recent.length === 0 ? (
          <p className="text-sm text-ink-muted">No messages yet.</p>
        ) : (
          <ul className="space-y-1">
            {recent.map((c) => (
              <MessageRow key={c.id} conversation={c} />
            ))}
          </ul>
        )}
      </WidgetBody>
    </PanelCard>
  );
}

function MessageRow({ conversation }: { conversation: InboxConversation }) {
  const last = conversation.messages[conversation.messages.length - 1];
  const preview = last ? `${last.author === 'me' ? 'You: ' : ''}${last.text}` : '';
  return (
    <li>
      <Link
        to={`/messages?c=${encodeURIComponent(conversation.id)}`}
        className="focus-ring flex gap-3 rounded-lg px-1 py-2 hover:bg-hover"
      >
        <MessageAvatar name={conversation.author.name} url={safeHref(conversation.author.avatarUrl)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'truncate text-sm',
                conversation.unread ? 'font-semibold text-ink' : 'font-medium text-ink',
              )}
            >
              {conversation.author.name}
            </span>
            {last && (
              <span className="ml-auto shrink-0 text-xs text-ink-faint">
                {formatRelative(last.sentAt)}
              </span>
            )}
            {conversation.unread && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-action" aria-label="Unread" />
            )}
          </div>
          <p
            className={cn(
              'mt-0.5 truncate text-sm',
              conversation.unread ? 'text-ink-strong' : 'text-ink-muted',
            )}
          >
            {preview}
          </p>
        </div>
      </Link>
    </li>
  );
}

function MessageAvatar({ name, url }: { name: string; url?: string }) {
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setFailed(true)}
        referrerPolicy="no-referrer"
        className="h-9 w-9 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-chip text-sm font-semibold text-ink-muted">
      {name.trim().charAt(0).toUpperCase() || '?'}
    </div>
  );
}

// ── Account Performance ─────────────────────────────────────────────────────────

/** Followers per connected ACCOUNT — two Pages on one platform are two bars. */
export function AccountPerformanceWidget() {
  const tenantKey = useTenantKey();
  const query = useQuery({
    queryKey: ['analytics-platforms', tenantKey],
    queryFn: ({ signal }) => api.getPlatformPerformance(signal),
    enabled: tenantKey !== '',
  });

  const rows = query.data ?? [];

  return (
    <LinkCard
      to="/analytics"
      icon={Users}
      title="Account Performance"
      isLoading={query.isLoading}
      isError={query.isError}
    >
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">No platform metrics yet.</p>
      ) : (
        <FollowersBarChart rows={rows} />
      )}
    </LinkCard>
  );
}

// ── Digest Sources ───────────────────────────────────────────────────────────

function SourcesWidget() {
  const tenantKey = useTenantKey();
  const query = useQuery({
    queryKey: ['sources', tenantKey],
    queryFn: ({ signal }) => api.listSources(signal),
    enabled: tenantKey !== '',
  });

  const sources = query.data ?? [];
  const issues = sources.filter(
    (s) => s.health === SourceHealth.FAILING || s.consecutiveFailures > 0,
  ).length;
  const totalItems = sources.reduce((sum, s) => sum + s.itemCount, 0);

  return (
    <LinkCard to="/sources" icon={Rss} title="Digest Sources" isLoading={query.isLoading} isError={query.isError}>
      <p className="text-sm text-ink">
        <span className="text-2xl font-bold leading-none text-ink">{sources.length}</span> sources
        {' · '}
        {totalItems.toLocaleString()} items
      </p>
      <div className="mt-3">
        {issues > 0 ? (
          <Badge tone="red">{issues} need attention</Badge>
        ) : (
          <Badge tone="green">All healthy</Badge>
        )}
      </div>
    </LinkCard>
  );
}

// ── Engagement Overview ────────────────────────────────────────────────────────

/**
 * The last 7 days for every connected account together ("All"), or for ONE account — one pill per
 * connected account, so two Pages on one platform are two pills with their own numbers.
 */
export function EngagementOverviewWidget() {
  const tenantKey = useTenantKey();
  const [picked, setPicked] = useState<string | undefined>();

  const connectionsQuery = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
  });
  const accounts = useMemo(
    () => analyticsAccounts(connectionsQuery.data ?? []),
    [connectionsQuery.data],
  );
  // An account disconnected since it was picked falls back to "All".
  const account = pickAccount(picked, accounts);

  const overview = useQuery({
    // The same key as /analytics — the account (or 'ALL'), then the range — so the two share a
    // cache entry at range=7.
    queryKey: ['analytics-overview', tenantKey, account ?? 'ALL', 7],
    queryFn: ({ signal }) => api.getAnalyticsOverview({ account, range: 7 }, signal),
    enabled: tenantKey !== '',
  });

  const data = overview.data;
  // Gate the engagement/reach headline on real SERIES data, not hasData — a followers-only
  // platform has hasData=true but no engagement/reach, and must show the notice, not a bare 0.
  const showTotals = data?.hasEngagementOrReach === true;
  const analyticsHref = account ? `/analytics?account=${account}&range=7` : '/analytics?range=7';

  return (
    <Card className="flex flex-col p-5">
      <CardHead
        icon={Flame}
        title="Engagement Overview"
        right={<span className="shrink-0 text-xs font-medium uppercase tracking-wide text-ink-faint">Last 7 days</span>}
      />

      {/* Account filter pills */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        <PlatformPill active={account == null} onClick={() => setPicked(undefined)} label="All" />
        {accounts.map((a) => {
          const meta = PLATFORM_META[a.platform];
          return (
            <PlatformPill
              key={a.connectionId}
              active={account === a.connectionId}
              onClick={() => setPicked(a.connectionId)}
              icon={<meta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              label={a.accountName}
              title={`${meta.label} - ${a.accountName}`}
            />
          );
        })}
      </div>

      <div className="mt-4">
        <WidgetBody isLoading={overview.isLoading} isError={overview.isError}>
          <Link to={analyticsHref} className="focus-ring block rounded-lg">
            <div className="flex items-end gap-6">
              <div>
                <p className="text-2xl font-bold leading-none text-ink">
                  {showTotals && data ? data.totals.engagements.toLocaleString() : '—'}
                </p>
                <p className="mt-1 text-xs text-ink-muted">Engagements</p>
              </div>
              <div>
                <p className="text-2xl font-bold leading-none text-link">
                  {showTotals && data ? data.totals.reach.toLocaleString() : '—'}
                </p>
                <p className="mt-1 text-xs text-ink-muted">Peak reach</p>
              </div>
            </div>
            <Sparkline series={data?.series ?? []} className="mt-3" />
          </Link>
          {!showTotals && (
            <p className="mt-2 text-xs text-ink-faint">
              {data?.notice ?? 'Metrics are still being collected — check back once your accounts report data.'}
            </p>
          )}
        </WidgetBody>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-line-soft pt-3 text-xs text-ink-faint">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 rounded bg-brand" /> Engagements
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 rounded bg-ink-ghost" /> Reach
          </span>
        </div>
        <HeadLink to={analyticsHref}>View Analytics</HeadLink>
      </div>
    </Card>
  );
}

function PlatformPill({
  active,
  onClick,
  icon,
  label,
  title,
}: {
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  label: string;
  /** "<Platform> - <account name>" for an account pill: the tooltip, and the accessible name. */
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      aria-label={title}
      className={cn(
        'focus-ring inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
        active ? 'bg-action text-action-fg' : 'border border-line-input bg-card text-ink-label hover:bg-hover',
      )}
    >
      {icon}
      {/* An account name can be longer than the card is wide; it shortens instead. */}
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );
}

// ── Upcoming Posts ───────────────────────────────────────────────────────────

export function UpcomingPostsWidget() {
  const tenantKey = useTenantKey();
  const approvalType = useEffectiveApprovalType();
  const query = useQuery({
    queryKey: ['posts', tenantKey],
    queryFn: ({ signal }) => api.listPosts(signal),
    enabled: tenantKey !== '',
  });

  /**
   * The next 3 posts awaiting release, soonest first. PAST-DUE ones are INCLUDED: a scheduled post
   * only leaves this list by being published, and nothing publishes it automatically unless the
   * workspace opted in to auto-publishing — so filtering them out (as this did) made a workspace
   * with a full calendar read "Nothing scheduled yet". They're flagged rather than hidden, since an
   * overdue post is the one most likely to need attention.
   */
  const upcoming = useMemo(() => {
    const now = Date.now();
    return (query.data ?? [])
      .filter((p) => p.scheduledAt != null && p.publishedAt == null)
      .sort((a, b) => Date.parse(a.scheduledAt as string) - Date.parse(b.scheduledAt as string))
      .slice(0, 3)
      .map((post) => ({ post, pastDue: Date.parse(post.scheduledAt as string) < now }));
  }, [query.data]);

  return (
    <PanelCard
      icon={Clock}
      title="Upcoming Posts"
      headLink={<HeadLink to="/campaigns?view=calendar">View calendar</HeadLink>}
    >
      <WidgetBody isLoading={query.isLoading} isError={query.isError}>
        {upcoming.length === 0 ? (
          <p className="text-sm text-ink-muted">Nothing scheduled yet.</p>
        ) : (
          <ul className="space-y-1">
            {upcoming.map(({ post, pastDue }) => {
              const state = postBadgeState(post, approvalType);
              const PlatformGlyph = post.platform ? PLATFORM_META[post.platform].Icon : FileText;
              const glyphColor = post.platform
                ? PLATFORM_META[post.platform].ink
                : 'text-ink-faint';
              return (
                <li key={post.id}>
                  <Link
                    to={`/?compose=${post.id}&ctab=schedule`}
                    className="focus-ring flex items-center gap-3 rounded-lg px-1 py-2 hover:bg-hover"
                  >
                    <PlatformGlyph className={cn('h-4 w-4 shrink-0', glyphColor)} aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">
                        {post.title || 'Untitled post'}
                      </p>
                      <p className={cn('text-xs', pastDue ? 'text-amber-600 dark:text-warning-dark' : 'text-ink-faint')}>
                        {formatDateTime(post.scheduledAt)}
                        {pastDue && ' · past due'}
                      </p>
                    </div>
                    <Badge tone={approvalStateTone[state]}>{approvalStateLabel[state]}</Badge>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </WidgetBody>

      <Link
        to="/?compose=new"
        className="focus-ring mt-3 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-line-dashed px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:border-ink-ghost hover:text-link"
      >
        <Plus className="h-4 w-4" aria-hidden /> New Post
      </Link>
    </PanelCard>
  );
}
